import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Button } from 'antd'
import * as Icons from '@ant-design/icons'
import produce from 'immer'
import _ from 'lodash'
import { useFormContext } from 'react-hook-form'

import {
  AddNewItem,
  CreationDate,
  Form,
  FormSectionTitle,
  FormSectionWrapper,
  SectionProvider
} from 'admin/lib/Forms'
import { HandleSingleAndDoubleClick } from 'admin/lib/Functions/handleSingleAndDoubleClick'
import {
  getLocaleFromCountry,
  isValidTimeZone
} from 'admin/lib/Functions/timeZoneHelper'
import { Flag } from 'admin/lib/Icons/Flag'

import { DeliveryLineItem } from './DeliveryLineItem'
import { DeliveryData } from './DeliveryData'
import {
  AddMultipleLineItemsModal,
  DuplicateRecordControls,
  FormControls,
  LastModifiedBy,
  RecordHistory,
  focusFirstError,
  useLineItemsManagement,
  useExtractionValueClicked,
  AttachmentView,
  difference,
  TemplateNameLabel,
  AutoPublishFailureReason
} from 'admin/pages/RecordEdit/common'

import {
  isoDateFromFormDate,
  isoDateToFormDate,
  cleanDate
} from 'admin/lib/dates'
import { Guid } from 'admin/lib/guid'
import { useAppInsights } from 'admin/lib/AppInsights/appinsights'
import { assembleLinkForMatchedRecord } from 'admin/pages/RecordEdit/common/assembleLinkForMatchedRecord'

const DEFAULT_PRODUCT = Object.freeze({
  id: null,
  name: '',
  height: '',
  heightUoM: 'Millimeter',
  width: '',
  widthUoM: 'Millimeter',
  length: '',
  lengthUoM: 'Millimeter',
  weight: '',
  weightUoM: 'Tonne',
  quantity: 1,
  productionTime: '',
  usedAtTime: '',
  calculatedVolume: '',
  givenVolume: '',
  givenVolumeUoM: 'CubicMeter',
  estimatedWeight: '',
  referenceProductId: Guid.Empty,
  givenCertifications: []
})

const DEFAULT_PRODUCT_IMPERIAL = Object.freeze({
  ...DEFAULT_PRODUCT,
  weightUoM: 'Pound',
  givenVolumeUoM: 'CubicYard'
})

export function Delivery({
  record,
  submit,
  project,
  isImperial,
  userAccountMappings,
  showHistory,
  onHistoryCloseClick
}) {
  const [showDebug, setShowDebug] = useState(false)
  const [isAddMultipleLineItemsOpen, setIsAddMultipleLineItemsOpen] =
    useState(false)

  const form = useFormContext()
  const appInsights = useAppInsights()
  const { moduleName, projectId, recordId, accountId } = useParams()
  const { reset, setValue } = form

  const projectCountry = project.country
  const projectTimeZone = project.timeZone ?? 'Europe/London'
  const projectLocale = getLocaleFromCountry(projectCountry)
  const MULTIATTACHMENT_WARNING_MESSAGE =
    'Multiple images uploaded with this record'

  const {
    methods,
    lineItemDuplicateHandlers,
    lineItemRemoveHandlers,
    handleAppendLineItem,
    handleAppendLineItemNoDebounce
  } = useLineItemsManagement(
    form,
    'data.products',
    'raw.products',
    isImperial ? DEFAULT_PRODUCT_IMPERIAL : DEFAULT_PRODUCT
  )

  const hasOneOrMoreLineItemWithProductGroupsContainingConcrete = () => {
    if (Array.isArray(methods.fields)) {
      return methods.fields.some(
        item =>
          item.productGroups &&
          Array.isArray(item.productGroups) &&
          item.productGroups.some(pg =>
            pg.name.toLowerCase().startsWith('concrete')
          )
      )
    }
    return false
  }

  const focusOnNewItem = () => {
    // focus on new element when ready
    const waitForInterval = setInterval(() => {
      const element = document.querySelector(
        `textarea[name^="uniquePrefix[${methods.fields.length}].name"`
      )
      if (element !== null) {
        clearInterval(waitForInterval)
        element.focus()
        const form = document.getElementsByTagName('form')[0]
        form.scrollTo({
          top: form.scrollHeight - 850,
          behavior: 'smooth'
        })
      }
    }, 500)
  }

  const openAddNewItemsModal = e => {
    setIsAddMultipleLineItemsOpen(true)
  }

  const closeAddNewItemsModal = e => {
    setIsAddMultipleLineItemsOpen(false)
  }

  const addNewLineItem = e => {
    handleAppendLineItem()
    focusOnNewItem()
  }

  const addMultipleNewLineItems = numOfNewItems => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        handleAppendLineItemNoDebounce()
      }
    }
  }

  const handleAddNewLineItem = HandleSingleAndDoubleClick(
    addNewLineItem,
    openAddNewItemsModal
  )

  const duplicateLineItem = item => {
    lineItemDuplicateHandlers(item)
    focusOnNewItem()
  }

  const duplicateLineItemMultiple = (numOfNewItems, originalItem) => {
    if (numOfNewItems > 0) {
      for (let i = 1; i <= numOfNewItems; i++) {
        lineItemDuplicateHandlers(originalItem)
      }
    }
  }

  const extractionValueClicked = useExtractionValueClicked(setValue)

  const matchedRecordLink = useMemo(() => {
    return assembleLinkForMatchedRecord(
      record.matchedRecord,
      'Delivery',
      userAccountMappings
    )
  }, [record.matchedRecord, userAccountMappings])

  useEffect(() => {
    reset(transformRecordToFormData(record, projectTimeZone))
  }, [projectTimeZone, record, reset])

  const toggleDebug = e => {
    if (e.detail === 3) {
      setShowDebug(s => !s)
    }
  }

  return (
    <SectionProvider>
      <Form
        id="form-panel"
        onSubmit={form.handleSubmit(
          formRecord =>
            submit(
              transformFormDataToRecord(record, formRecord, appInsights, {
                projectId,
                recordId,
                projectTimeZone
              })
            ),
          () => focusFirstError(form.errors, {})
        )}
      >
        {record.recordSimilarityDetails && (
          <DuplicateRecordControls
            recordSimilarityDetails={record.recordSimilarityDetails}
            recordStatus={record.status}
          />
        )}

        <TemplateNameLabel
          templateName={record.templateName}
          estimatedAccuracy={record.estimatedAccuracy}
          link={matchedRecordLink}
        />
        <AutoPublishFailureReason
          autoPublishFailureReasons={record.autoPublishFailureReasons}
          recordStatus={record.status}
        />
        {record.attachmentPages > 1 && (
          <p className="warning">{MULTIATTACHMENT_WARNING_MESSAGE}</p>
        )}
        <FormControls />
        <LastModifiedBy
          modifier={record.modifier}
          modifiedById={record.modifiedBy}
        />
        {record.migrationSource !== null && (
          <p>
            Migrated from recordId: {record.migrationSource.recordId}
            <br />
            Migration Reason: {record.migrationSource.migrationReason}
          </p>
        )}
        <FormSectionWrapper>
          <FormSectionTitle level={5} onClick={toggleDebug}>
            <Flag country={projectCountry} /> Delivery
          </FormSectionTitle>
          <CreationDate>
            <>
              <div style={{ fontWeight: '600' }}>Creation Date</div>
              <div style={{ lineHeight: '1.2', margin: '4px 0' }}>
                {`${
                  projectTimeZone &&
                  isValidTimeZone(projectTimeZone) &&
                  Intl.DateTimeFormat().resolvedOptions().timeZone !==
                    projectTimeZone
                    ? 'Local: '
                    : ''
                }${new Date(record.createdAt).toLocaleString([], {
                  timeZoneName: 'short',
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric'
                })}`}
                {
                  // If Project Locale differs from local Locale
                  // Or project TimeZone differs from local TimeZone
                  (projectLocale !==
                    Intl.DateTimeFormat().resolvedOptions().locale ||
                    (projectTimeZone &&
                      isValidTimeZone(projectTimeZone) &&
                      Intl.DateTimeFormat().resolvedOptions().timeZone !==
                        projectTimeZone)) && (
                    <>
                      <br />
                      Project:{' '}
                      {new Date(record.createdAt).toLocaleString(
                        projectLocale,
                        {
                          timeZone: projectTimeZone,
                          timeZoneName: 'short',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: 'numeric',
                          minute: 'numeric'
                        }
                      )}
                    </>
                  )
                }
              </div>
            </>
          </CreationDate>
        </FormSectionWrapper>
        {showDebug && (
          <div
            style={{ marginBottom: '5px', fontSize: 'smaller' }}
            tabindex="-1"
          >
            <dl>
              <dt>Journey distance (mi)</dt>
              <dd>{record.data.journey?.distanceMiles}</dd>
            </dl>
            <dl>
              <dt>
                Laden journey emissions (kg CO<sub>2</sub>e)
              </dt>
              <dd>{record.data.journey?.ladenEmissionsKgCO2e}</dd>
            </dl>
            <dl>
              <dt>
                Unladen journey emissions (kg CO<sub>2</sub>e)
              </dt>
              <dd>{record.data.journey?.unladenEmissionsKgCO2e}</dd>
            </dl>
            {record.data.products.map((p, i) => (
              <dl>
                <dt>
                  Product ({i + 1}) Embodied Carbon (kg CO<sub>2</sub>e)
                </dt>
                <dd>{p.embodiedCarbonKgCO2e}</dd>
              </dl>
            ))}
          </div>
        )}
        <DeliveryData
          createdAt={record.createdAt}
          projectName={project.name}
          projectTimeZone={projectTimeZone}
          projectCountry={project.country}
          projectId={projectId}
          isImperial={isImperial}
          hasOneOrMoreLineItemWithProductGroupsContainingConcrete={hasOneOrMoreLineItemWithProductGroupsContainingConcrete()}
        />
        <FormSectionWrapper>
          <FormSectionTitle level={5}>
            <Flag country={projectCountry} /> Products (
            {
              methods.fields.filter(item => !item.isDeleted && !item.isRemoved)
                .length
            }
            )
          </FormSectionTitle>
          <AddNewItem onClick={handleAddNewLineItem}>
            Add new product
          </AddNewItem>
        </FormSectionWrapper>
        {methods.fields.map((item, i) => (
          <DeliveryLineItem
            key={item.hookFormId}
            index={i}
            item={item}
            onDuplicate={duplicateLineItem}
            onDuplicateMultiple={duplicateLineItemMultiple}
            onRemove={lineItemRemoveHandlers[i]}
            isImperial={isImperial}
          />
        ))}
        <Button
          onClick={handleAddNewLineItem}
          block
          icon={<Icons.PlusCircleFilled />}
          type="dashed"
          htmlType="button"
        >
          Add new product
        </Button>

        <Button
          onClick={e => (document.getElementById('form-panel').scrollTop = 0)}
          style={{ marginTop: '1rem' }}
        >
          Back to top
        </Button>
      </Form>

      <AttachmentView
        record={record}
        handleExtractionValueClicked={extractionValueClicked}
      />
      {record.history && (
        <RecordHistory
          changes={record.history.changes}
          moduleName={moduleName}
          isImperial={isImperial}
          projectLocale={projectLocale}
          projectTimeZone={projectTimeZone}
          lineItemCount={record.data.products.length}
          onCloseClick={onHistoryCloseClick}
          showHistory={showHistory}
          accountId={accountId}
          projectId={projectId}
          recordId={recordId}
        />
      )}

      <AddMultipleLineItemsModal
        moduleName={moduleName}
        isOpen={isAddMultipleLineItemsOpen}
        onClose={closeAddNewItemsModal}
        onSubmit={val => {
          addMultipleNewLineItems(val)
        }}
        duplicateLineItemName={null}
      />
    </SectionProvider>
  )
}

Delivery.propTypes = {
  record: PropTypes.object,
  submit: PropTypes.func.isRequired,
  userAccountMappings: PropTypes.array
}

//
// Transforms

function transformFormDataToRecord(
  record,
  formRecord,
  appInsights,
  { projectId, recordId, projectTimeZone }
) {
  const diffArray = difference(record, formRecord)
  diffArray.forEach(diff =>
    appInsights.trackEvent(
      { name: 'new-admin_update_field' },
      {
        status: formRecord.status,
        field: diff.field,
        new_value: diff.newValue ?? null,
        old_value: diff.oldValue ?? null,
        module: 'Delivery',
        project_id: projectId,
        record_id: recordId
      }
    )
  )
  appInsights.flush()

  return produce(_.merge({}, record, formRecord), draft => {
    draft.data.deliveryArrivalDate = isoDateFromFormDate(
      draft.data.deliveryArrivalDate,
      projectTimeZone
    )
    draft.data.deliveryExitDate = isoDateFromFormDate(
      draft.data.deliveryExitDate,
      projectTimeZone
    )
    draft.assignee = formRecord.assignee

    draft.data.products = formRecord.data.products
  })
}

function transformRecordToFormData(record, projectTimeZone) {
  return produce(record, draft => {
    draft.data.deliveryArrivalDate = cleanDate(draft.data.deliveryArrivalDate)
    draft.data.deliveryExitDate = cleanDate(draft.data.deliveryExitDate)
    draft.data.deliveryArrivalDate = isoDateToFormDate(
      draft.data.deliveryArrivalDate,
      projectTimeZone
    )
    draft.data.deliveryExitDate = isoDateToFormDate(
      draft.data.deliveryExitDate,
      projectTimeZone
    )

    for (const product of draft.data.products ?? []) {
      product.usedAtTime = cleanDate(product.usedAtTime)
      product.productionTime = cleanDate(product.productionTime)
      product.productionTime = isoDateToFormDate(
        product.productionTime,
        projectTimeZone
      )
      product.usedAtTime = isoDateToFormDate(
        product.usedAtTime,
        projectTimeZone
      )
    }
  })
}
